import React, { useCallback, useMemo, useRef, useState } from 'react';
import GridItem from '../../../../layout/GridComponents/GridItem';
import {
  NavReconOverviewExpandedObject,
  NavReconPipelineMetadata,
} from '../../../../../api/rs-hg/nav-recon/types';
import { sortFundNames } from '../../../../../utilities/customSorting';
import {
  NavReconOverviewTableRow,
  getNavReconOverviewColumns,
} from '../navReconOverviewColumns';
import NavReconOverviewDetailPanel from './NavReconOverviewDetailPanel';
import {
  buildAuditMetadataByFund,
  buildFundPipelineMetadataByFundName,
  buildNavReceivedTimestampByFund,
  buildPipelineProgressByFund,
  getNavReconFundPipelineProgressForFundName,
} from '../pipelineMetadata';
import { parseUtcTimestampMs } from '../../../dashboard/utils/date.utils';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  ExpandedState,
  SortingState,
} from '@tanstack/react-table';
import SearchBar from '../../../../ui/inputs/SearchBar';
import ExportButton from '../../../../feedback/ExportButton';
import { HiChevronUp, HiChevronDown, HiOutlineChevronUpDown } from 'react-icons/hi2';
import NavReconOverviewStats from './NavReconOverviewStats';
import {
  columnWidthStyle,
  getColumnAlignMeta,
} from '../../../../tables/tanstack/tanstackUtils';

interface Props {
  data: NavReconOverviewExpandedObject[];
  pipelineMetadata: NavReconPipelineMetadata[];
  positionDate: string;
  section: string;
}

/**
 * `NavReconOverviewExpandedObject[]` grid for the default NAV-Recon overview
 * route (`get_nav_recon_overview2`).
 * Refactored to use @tanstack/react-table directly like the raidr v2 overview page.
 */
const NavReconOverviewTable: React.FC<Props> = ({
  data,
  pipelineMetadata,
  positionDate,
  section,
}) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const sortingRef = useRef(sorting);
  sortingRef.current = sorting;
  const getSorting = useCallback(() => sortingRef.current, []);

  const auditMetadataByFund = useMemo(
    () => buildAuditMetadataByFund(pipelineMetadata),
    [pipelineMetadata],
  );

  const pipelineProgressByFund = useMemo(
    () => buildPipelineProgressByFund(pipelineMetadata),
    [pipelineMetadata],
  );

  const fundPipelineMetaByName = useMemo(
    () => buildFundPipelineMetadataByFundName(pipelineMetadata),
    [pipelineMetadata],
  );

  const navReceivedByFund = useMemo(
    () => buildNavReceivedTimestampByFund(pipelineMetadata),
    [pipelineMetadata],
  );

  const rows = useMemo<NavReconOverviewTableRow[]>(
    () =>
      [...data]
        .sort((a, b) => sortFundNames(a.fund_name || '', b.fund_name || ''))
        .map((item, index) => {
          const auditStatus =
            auditMetadataByFund[item.fund_name]?.clientAuditStatus ?? null;
          const isApproved =
            String(auditStatus ?? '').toLowerCase() === 'approved';
          const calculationTimestamp =
            navReceivedByFund[item.fund_name] ?? null;
          // Approved funds have no active timer — clear timestamps so sort
          // treats them as empty (same as no NAV received).
          const navReceivedTimestamp = isApproved
            ? null
            : calculationTimestamp;
          return {
            ...(item.data ?? {}),
            fund_name: item.fund_name,
            fund_id: item.fund_id,
            fund_name_full: item.fund_name_full ?? '',
            subfund_code: item.subfund_code,
            index,
            overviewHasPayload:
              item.data != null && Object.keys(item.data).length > 0,
            auditStatus,
            auditSnapshotTimestamp:
              auditMetadataByFund[item.fund_name]?.snapshotTimestamp ?? null,
            auditClientComment:
              auditMetadataByFund[item.fund_name]?.clientComment ?? null,
            auditClientAuditByName:
              auditMetadataByFund[item.fund_name]?.clientAuditByName ?? null,
            calculationTimestamp,
            calculationAtMs: parseUtcTimestampMs(calculationTimestamp),
            navReceivedTimestamp,
            navReceivedAtMs: parseUtcTimestampMs(navReceivedTimestamp),
            pipelineProgress: getNavReconFundPipelineProgressForFundName(
              item.fund_name,
              pipelineProgressByFund,
            ),
            pipelineFundMetadata: fundPipelineMetaByName[item.fund_name],
          };
        }),
    [
      auditMetadataByFund,
      data,
      fundPipelineMetaByName,
      navReceivedByFund,
      pipelineProgressByFund,
    ],
  );

  const columns = useMemo(
    () => getNavReconOverviewColumns(positionDate, section, getSorting),
    [positionDate, section, getSorting],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
      expanded,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
  } as any);

  const { csvFields, fieldsMap } = useMemo(() => {
    const exportable = columns.filter(
      (col: any) => col.accessorKey && col.accessorKey !== 'index' && col.accessorKey !== 'expand'
    );
    return {
      csvFields: exportable.map((col: any) => col.accessorKey as string),
      fieldsMap: exportable.map((col: any) => ({
        key: col.accessorKey as string,
        label: col.header as string,
      })),
    };
  }, [columns]);

  return (
    <GridItem
      card
      xs={12}
      height="calc(100vh - 9rem)"
      cardStyle={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="border-b border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex-1 w-full min-w-0">
            <NavReconOverviewStats
              title="NAV-Recon Overview"
              rows={rows}
            />
          </div>
          <div className="flex items-center space-x-3 w-full md:w-auto justify-end flex-shrink-0">
            <SearchBar
              searchHandler={setGlobalFilter}
              placeholder="Search overview..."
              className="py-1 text-xs max-w-[180px]"
            />
            <ExportButton
              exportData={rows}
              pdfIdentifier="nav-recon-overview"
              fields={csvFields}
              fieldsMap={fieldsMap}
              selectedPositionDate={positionDate}
              allowPdfExport={true}
              linkToRaptorExport={true}
            />
          </div>
        </div>
      </div>

      <div className="overflow-auto flex-1 min-h-0 w-full relative">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-mainblue text-white shadow-sm">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = getColumnAlignMeta(header.column.columnDef.meta);
                  const style = columnWidthStyle(meta);
                    return (
                      <th
                        key={header.id}
                        style={style}
                        className={(() => {
                          let baseClass = 'px-1.5 py-2 select-none cursor-pointer border-b border-blue-800 hover:bg-white/10 sticky top-0 z-10 bg-mainblue ';
                          if (meta?.align === 'center') {
                            return baseClass + 'text-center text-[10px] font-bold uppercase tracking-wider';
                          }
                          return baseClass + 'text-left text-[10px] font-bold uppercase tracking-wider';
                        })()}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div
                          className="relative flex items-center w-full group/header"
                        >
                          <span className={`w-full font-semibold text-[10px] uppercase tracking-wider text-blue-100 group-hover/header:text-white transition-colors duration-150 pr-2.5 ${
                            meta?.align === 'center' ? 'text-center' : 'text-left'
                          }`}>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </span>
                          {header.column.getCanSort() && (
                            <span className="absolute right-0 top-1/2 -translate-y-1/2 transition-all duration-200 flex-shrink-0">
                              {header.column.getIsSorted() === 'asc' ? (
                                <HiChevronUp className="w-3.5 h-3.5 text-white" />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <HiChevronDown className="w-3.5 h-3.5 text-white" />
                              ) : (
                                <HiOutlineChevronUpDown className="w-3.5 h-3.5 text-blue-300/40 group-hover/header:text-blue-200" />
                              )}
                            </span>
                          )}
                        </div>
                      </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center text-gray-500 text-sm"
                >
                  No funds found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const rowData = row.original as NavReconOverviewTableRow;
                const isInactive =
                  !rowData.overviewHasPayload ||
                  rowData.pipelineProgress?.status === 'pending';
                const rowClass = isInactive
                  ? 'bg-gray-100 text-gray-500 border-b border-gray-200'
                  : 'bg-white hover:bg-gray-50 border-b border-gray-200';

                return (
                  <React.Fragment key={row.id}>
                    <tr className={rowClass}>
                      {row.getVisibleCells().map((cell) => {
                        const meta = getColumnAlignMeta(cell.column.columnDef.meta);
                        const style = columnWidthStyle(meta);
                        return (
                          <td
                            key={cell.id}
                            style={style}
                            className={`px-1.5 py-1.5 text-xs border-b border-gray-100 ${
                              meta?.align === 'center' ? 'text-center' : 'text-left'
                            }`}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {row.getIsExpanded() && (
                      <tr>
                        <td colSpan={columns.length} className="bg-gray-50 p-0">
                          <NavReconOverviewDetailPanel row={rowData} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </GridItem>
  );
};

export default NavReconOverviewTable;
